/**
 * Utility functions for Discussion Room Brightcove Block.
 */

export const TITLE_AND_RUNTIME_REGEX = /^(.*?)\s*(?:\((\d+(?::\d+)?)\))?$/;
export const UP_NEXT_DISPLAY_THRESHOLD_SECONDS = 6;

/**
 * Parses a title string for episode title and optional parenthesized runtime.
 *
 * @param {string|null|undefined} titleAttr Title attribute value (e.g. "Episode 1 (12:45)").
 * @return {RegExpMatchArray|null} Regex match result or null.
 */
export function parseTitleAndRuntime( titleAttr ) {
	if ( typeof titleAttr !== 'string' ) {
		return null;
	}
	return titleAttr.match( TITLE_AND_RUNTIME_REGEX );
}

/**
 * Extracts structured title and runtime strings from a title attribute.
 *
 * @param {string|null|undefined} titleAttr
 * @return {{title: string, runtime: string}}
 */
export function extractTitleAndRuntime( titleAttr ) {
	const match = parseTitleAndRuntime( titleAttr );
	if ( ! match ) {
		return {
			title: typeof titleAttr === 'string' ? titleAttr.trim() : '',
			runtime: '',
		};
	}
	return {
		title: ( match[1] || '' ).trim(),
		runtime: match[2] || '',
	};
}

/**
 * Resolves the next episode metadata from the episode list container.
 *
 * @param {Element|null|undefined} episodeList Episode list DOM container.
 * @return {{nextEpisode: Element, nextEpisodeUrl: string, nextEpisodeTitle: string, nextEpisodeRuntime: string}|null}
 */
export function getNextEpisodeData( episodeList ) {
	if ( ! episodeList ) {
		return null;
	}
	const selected = episodeList.querySelector( '.episode-number.selected' );
	const nextEpisode = selected?.nextElementSibling;
	if ( ! nextEpisode ) {
		return null;
	}

	const nextEpisodeUrl = nextEpisode.href || nextEpisode.getAttribute( 'href' ) || '';
	const nextEpisodeAttributes = nextEpisode.getAttribute( 'title' ) || '';
	const parsed = extractTitleAndRuntime( nextEpisodeAttributes );
	let nextEpisodeTitle = parsed.title;
	let nextEpisodeRuntime = parsed.runtime;

	const videoLength = nextEpisode.querySelector( '.video-length' );
	if ( videoLength && videoLength.textContent ) {
		nextEpisodeRuntime = videoLength.textContent.replace( /\(|\)/g, '' ).trim();
	}

	return {
		nextEpisode,
		nextEpisodeUrl,
		nextEpisodeTitle,
		nextEpisodeRuntime,
	};
}

/**
 * Calculates remaining video playback time in seconds.
 *
 * @param {number} duration Video duration in seconds.
 * @param {number} currentTime Current playback time in seconds.
 * @return {number} Remaining seconds.
 */
export function calculateRemainingTime( duration, currentTime ) {
	if ( typeof duration !== 'number' || typeof currentTime !== 'number' ) {
		return 0;
	}
	return duration - currentTime;
}

/**
 * Determines whether the remaining playback time is within the Up Next display threshold.
 *
 * @param {number} remainingTime Remaining playback time in seconds.
 * @param {number} [threshold=UP_NEXT_DISPLAY_THRESHOLD_SECONDS] Threshold in seconds.
 * @return {boolean} True if within threshold.
 */
export function isWithinUpNextThreshold( remainingTime, threshold = UP_NEXT_DISPLAY_THRESHOLD_SECONDS ) {
	return typeof remainingTime === 'number' && remainingTime <= threshold;
}

/**
 * Attaches a dismissal click handler to the Up Next close button.
 *
 * @param {Element|null|undefined} closeButton The close button element.
 * @param {Element|null|undefined} upNextContainer The Up Next container element.
 * @param {Function} [onDismiss] Optional callback invoked on dismissal.
 */
export function setupUpNextCloseHandler( closeButton, upNextContainer, onDismiss ) {
	if ( ! closeButton || ! upNextContainer ) {
		return;
	}
	closeButton.addEventListener( 'click', function ( e ) {
		e.preventDefault();
		e.stopPropagation();
		upNextContainer.style.display = 'none';
		if ( typeof onDismiss === 'function' ) {
			onDismiss();
		}
	} );
}

/**
 * Checks whether autoadvance is enabled on the player element.
 *
 * @param {Element|null|undefined} playerElement Video element.
 * @return {boolean}
 */
export function isAutoadvanceEnabled( playerElement ) {
	if ( ! playerElement || typeof playerElement.getAttribute !== 'function' ) {
		return false;
	}
	return playerElement.getAttribute( 'data-autoadvance' ) === '1';
}
